import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  FileTextIcon,
  ClipboardListIcon,
  BriefcaseIcon,
  DollarSignIcon,
  MapPinIcon,
  HomeIcon,
  TrashIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { ColumnSelector } from '../components/ColumnSelector'
import { ListFilterSortBar } from '../components/ListFilterSortBar'
import { useColumnSelector } from '../hooks/useColumnSelector'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileStatStrip,
  mobile,
} from '../components/mobile'
import { usePermissions } from '../hooks/usePermissions'
import { usesFieldHome } from '../lib/fieldHome'
import {
  EMPTY_DATE_RANGE,
  matchesDateRange,
  matchesMultiSelect,
  sortItems,
  uniqueFilterOptions,
  type DateRange,
  type SortDir,
} from '../lib/listFilterSort'

export function Properties() {
  const navigate = useNavigate()
  const { profile, isSuperAdmin, loading: permLoading } = usePermissions()
  const fieldHome = usesFieldHome(profile?.role, isSuperAdmin)
  const [properties, setProperties] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [customerFilter, setCustomerFilter] = useState<string[]>([])
  const [createdRange, setCreatedRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*, customers!inner(company_name)')
        .order('created_at', { ascending: false })

      if (error) throw error

      // Transform the data to match the expected format
      const transformedData =
        data?.map((p) => ({
          id: p.id,
          name: p.name,
          address: `${p.address || ''} ${p.city || ''} ${p.state || ''} ${p.zip_code || ''}`.trim(),
          customer: (p.customers as any)?.company_name || 'N/A',
          customerId: p.customer_id,
          type: p.property_type || 'N/A',
          sqft: p.sqft || 0,
          jobs: 0,
          created_at: p.created_at || '',
          lastService: p.created_at ? new Date(p.created_at).toISOString().split('T')[0] : '',
          status: p.is_active === false ? 'inactive' : 'active',
        })) || []

      setProperties(transformedData)
    } catch (error) {
      console.error('Error fetching properties:', error)
      alert('Error loading properties: ' + (error as any).message)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProperty = async (property: any) => {
    const label = property.name || property.address || 'this property'
    if (
      !window.confirm(
        `Are you sure you want to delete "${label}"? Jobs, contracts, and other records linked to this property may block deletion. This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      const { error } = await supabase.from('properties').delete().eq('id', property.id)
      if (error) throw error
      setProperties((prev) => prev.filter((p) => p.id !== property.id))
    } catch (error: any) {
      console.error('Error deleting property:', error)
      const msg = error?.message || ''
      if (msg.includes('foreign key') || msg.includes('referenced')) {
        alert(
          'Cannot delete this property because it is linked to jobs, contracts, or other records. Remove those links first.'
        )
      } else {
        alert('Failed to delete property: ' + (msg || 'Unknown error'))
      }
    }
  }

  const PROPERTY_COLUMNS = [
    { id: 'property', label: 'Property' },
    { id: 'customer', label: 'Customer' },
    { id: 'type', label: 'Type' },
    { id: 'size', label: 'Size' },
    { id: 'jobs', label: 'Jobs' },
    { id: 'last_service', label: 'Last Service' },
    { id: 'status', label: 'Status' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(PROPERTY_COLUMNS, { storageKey: 'properties' })

  useEffect(() => {
    if (permLoading || fieldHome) return
    fetchProperties()
  }, [permLoading, fieldHome])

  const typeOptions = uniqueFilterOptions(
    properties.map((p) => (p.type !== 'N/A' ? p.type : null))
  )
  const customerOptions = uniqueFilterOptions(
    properties.map((p) => (p.customer !== 'N/A' ? p.customer : null))
  )

  const filteredProperties = sortItems(
    properties.filter((property) => {
      const matchesSearch =
        property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.customer.toLowerCase().includes(searchTerm.toLowerCase())
      return (
        matchesSearch &&
        matchesMultiSelect(property.type, typeFilter) &&
        matchesMultiSelect(property.status, statusFilter) &&
        matchesMultiSelect(property.customer, customerFilter) &&
        matchesDateRange(property.created_at, createdRange)
      )
    }),
    (property) => {
      switch (sortBy) {
        case 'customer':
          return property.customer || ''
        case 'type':
          return property.type || ''
        case 'size':
          return property.sqft || 0
        case 'created':
          return property.created_at || ''
        default:
          return property.name || ''
      }
    },
    sortDir
  )

  if (!permLoading && fieldHome) {
    return <Navigate to="/my-properties" replace />
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Properties"
        subtitle={<span className="hidden sm:inline">Manage property locations and details</span>}
        actions={
          <button
            type="button"
            onClick={() => navigate('/properties/create')}
            className={mobile.primaryBtn}
          >
            <PlusIcon className="w-5 h-5" />
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Property</span>
          </button>
        }
      />

      <div className="lg:hidden">
        <MobileStatStrip
          stats={[
            { key: 'total', label: 'Total', value: properties.length, tone: 'blue' },
            {
              key: 'active',
              label: 'Active',
              value: properties.filter((p) => p.status === 'active').length,
              tone: 'green',
            },
            {
              key: 'jobs',
              label: 'Jobs',
              value: properties.reduce((sum, p) => sum + (p.jobs || 0), 0),
            },
          ]}
        />
      </div>

        {/* Stats Cards */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Properties</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{properties.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                <HomeIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Properties</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {properties.filter((p) => p.status === 'active').length}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-green-50 text-green-600">
                <HomeIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sq Ft</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {properties.reduce((sum, p) => sum + p.sqft, 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-violet-50 text-violet-600">
                <MapPinIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Jobs</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {properties.reduce((sum, p) => sum + p.jobs, 0)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-orange-50 text-orange-600">
                <BriefcaseIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        <ListFilterSortBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search properties..."
          className="lg:p-6"
          filters={[
            {
              type: 'multi',
              label: 'Type',
              value: typeFilter,
              onChange: setTypeFilter,
              options: typeOptions,
            },
            {
              type: 'multi',
              label: 'Status',
              value: statusFilter,
              onChange: setStatusFilter,
              options: [
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ],
            },
            {
              type: 'multi',
              label: 'Customer',
              value: customerFilter,
              onChange: setCustomerFilter,
              options: customerOptions,
            },
            {
              type: 'daterange',
              label: 'Created',
              value: createdRange,
              onChange: setCreatedRange,
            },
          ]}
          sortValue={sortBy}
          onSortChange={setSortBy}
          sortOptions={[
            { value: 'name', label: 'Name' },
            { value: 'customer', label: 'Customer' },
            { value: 'type', label: 'Type' },
            { value: 'size', label: 'Size' },
            { value: 'created', label: 'Created' },
          ]}
          sortDir={sortDir}
          onSortDirChange={setSortDir}
          trailing={
            <div className="hidden lg:block">
              <ColumnSelector
                columns={PROPERTY_COLUMNS}
                visibleColumns={visibleColumns}
                onToggle={toggleColumn}
              />
            </div>
          }
        />

        {/* Mobile cards */}
        <div className="native-mobile-list space-y-2">
          {filteredProperties.length === 0 ? (
            <MobileEmptyState title="No properties found" description="Try a different search." />
          ) : (
            filteredProperties.map((property) => (
              <MobileListRow
                key={property.id}
                onClick={() => navigate(`/properties/view/${property.id}`)}
                title={property.name}
                subtitle={property.customer}
                badges={
                  <span
                    className={`${mobile.badge} ${
                      property.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {property.status}
                  </span>
                }
                meta={
                  <div className="flex items-center gap-1 truncate">
                    <MapPinIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{property.address}</span>
                  </div>
                }
              />
            ))
          )}
        </div>

        {/* Desktop table */}
        <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Property List</h2>
          </div>
          <div className="w-full">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('property') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                  )}
                  {visibleColumns.has('customer') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                  )}
                  {visibleColumns.has('type') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                  )}
                  {visibleColumns.has('size') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                  )}
                  {visibleColumns.has('jobs') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Jobs
                    </th>
                  )}
                  {visibleColumns.has('last_service') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Service
                    </th>
                  )}
                  {visibleColumns.has('status') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.has('actions') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProperties.map((property) => (
                  <tr key={property.id} className="hover:bg-gray-50">
                    {visibleColumns.has('property') && (
                      <td className="px-3 py-3 min-w-0">
                        <div>
                          <button
                            onClick={() => navigate(`/properties/view/${property.id}`)}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left truncate block max-w-full"
                          >
                            {property.name}
                          </button>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPinIcon className="w-3 h-3" />
                            {property.address}
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.has('customer') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900 truncate">
                        {property.customer}
                      </td>
                    )}
                    {visibleColumns.has('type') && (
                      <td className="px-3 py-3 min-w-0">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {property.type}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('size') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900 truncate">
                        {property.sqft.toLocaleString()} sq ft
                      </td>
                    )}
                    {visibleColumns.has('jobs') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900 truncate">
                        {property.jobs}
                      </td>
                    )}
                    {visibleColumns.has('last_service') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900 truncate">
                        {new Date(property.lastService).toLocaleDateString()}
                      </td>
                    )}
                    {visibleColumns.has('status') && (
                      <td className="px-3 py-3 min-w-0">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            property.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {property.status}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('actions') && (
                      <td className="px-3 py-3 min-w-0">
                        <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/proposals/create?property=${property.id}`)}
                          className="text-violet-600 hover:text-violet-900"
                          title="Create Proposal"
                        >
                          <FileTextIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/contracts/create?property=${property.id}`)}
                          className="text-green-600 hover:text-green-900"
                          title="Create Contract"
                        >
                          <ClipboardListIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/jobs/create?property_id=${property.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Create Job"
                        >
                          <BriefcaseIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/invoice-generation?property=${property.id}`)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Create Invoice"
                        >
                          <DollarSignIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProperty(property)}
                          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete Property"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    </MobilePage>
  )
}
